import { cs } from '../../../lib/currency';
/**
 * iter307cd — Edit / Create Manual Billing Cycle dialogs.
 *
 * Mounted inside EnrollmentBillingTab. Two dialogs:
 *   1. EditBillingCycleDialog — edits an UNPAID cycle (amount + dates + meta).
 *      Blocks paid/refunded/void (server also enforces, double-belt).
 *      Optional cascade toggle: apply the new amount to every future unpaid cycle.
 *
 *   2. NewBillingCycleDialog — creates a new manual cycle. Always creates a
 *      monthly_invoice in pending status (per user requirement — no "skip
 *      invoice" branch). Server guards backdating + duplicate (enrollment, month).
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Checkbox } from '../../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../services/api';

const _PAID_LIKE = new Set(['paid', 'refunded', 'partially_refunded', 'void', 'pending_refund', 'superseded']);
// iter307ce — paid/processing block delete; pending/overdue allow void.
const _DELETE_BLOCKED_PAID = new Set(['paid', 'refunded', 'partially_refunded', 'pending_refund']);
const _DELETE_BLOCKED_PROCESSING = new Set(['processing']);

const _currentYearMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

export const isCycleEditable = (cycle) => {
  if (!cycle) return false;
  const s = (cycle.display_status || cycle.status || '').toLowerCase();
  return !_PAID_LIKE.has(s);
};

export const isCycleDeletable = (cycle) => {
  if (!cycle) return { ok: false, reason: 'No cycle' };
  const s = (cycle.display_status || cycle.status || '').toLowerCase();
  // iter307cf — already-void rows are still clickable (re-stamp path to
  // heal data drift). Frontend lets the click through; backend re-stamps
  // void fields and returns restamped=true.
  if (s === 'void' || s === 'superseded') {
    return { ok: true, reason: 'Already void — click to re-stamp void fields', alreadyVoid: true };
  }
  if (_DELETE_BLOCKED_PAID.has(s)) {
    // iter360d — a FULLY-REFUNDED cycle (net paid $0) holds no money and
    // MUST be deletable (soft-void). A refund still pending stays blocked.
    if (s === 'pending_refund' || cycle.pending_refund) {
      return { ok: false, reason: 'A refund is still pending on this cycle. Wait for it to settle first.', paid: true };
    }
    const charged = Number(cycle.amount_charged) || 0;
    const refunded = Number(cycle.refunded_amount ?? cycle.refund_amount) || 0;
    const netPaid = cycle.net_paid != null ? Number(cycle.net_paid) : Math.max(0, charged - refunded);
    const fullyRefunded = charged > 0 && refunded > 0 && netPaid <= 0.005;
    if (fullyRefunded) {
      return { ok: true, reason: 'Fully refunded (net $0) — safe to delete (voids the cycle).', fullyRefunded: true };
    }
    return { ok: false, reason: 'Cannot delete a paid cycle. Issue an Account Credit or process a refund first.', paid: true };
  }
  if (_DELETE_BLOCKED_PROCESSING.has(s)) {
    return { ok: false, reason: 'Cycle is processing in Stripe. Wait for settlement or cancel the PaymentIntent first.', processing: true };
  }
  return { ok: true };
};

export const EditBillingCycleDialog = ({ open, onOpenChange, enrollmentId, cycle, onSaved }) => {
  const [form, setForm] = useState({});
  const [cascade, setCascade] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmStep, setConfirmStep] = useState(false);

  useEffect(() => {
    if (open && cycle) {
      setForm({
        amount: cycle.amount ?? '',
        due_date: cycle.due_date || '',
        description: cycle.description || '',
        billing_month: cycle.billing_month || '',
        period_start: cycle.period_start || '',
        period_end: cycle.period_end || '',
        // iter307dc — Frequency on the cycle row (may differ from
        // the enrollment's current frequency for historical cycles).
        payment_frequency: (cycle.payment_frequency || 'monthly').toLowerCase(),
      });
      setCascade(false);
      setConfirmStep(false);
    }
  }, [open, cycle]);

  if (!cycle) return null;

  const blocked = !isCycleEditable(cycle);
  // iter307ce — past-cycle warning: when the cycle's billing_month is
  // before the current YYYY-MM, surface an amber banner so admins know
  // they're modifying historical data.
  const isPastCycle = (cycle.billing_month || '') < _currentYearMonth();

  const handleSave = async () => {
    if (blocked) return;
    if (!confirmStep) {
      setConfirmStep(true);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        amount: form.amount === '' ? null : Number(form.amount),
        due_date: form.due_date || null,
        description: form.description || null,
        billing_month: form.billing_month || null,
        period_start: form.period_start || null,
        period_end: form.period_end || null,
        // iter307dc — Frequency change re-stamps MI.payment_frequency
        // + frequency_multiplier (enrollment row is unaffected).
        payment_frequency: form.payment_frequency || null,
        frequency_multiplier: ({monthly: 1, quarterly: 3, biannual: 6}[form.payment_frequency] || null),
        cascade_to_future_unpaid: cascade,
      };
      const { data } = await api.patch(
        `/admin/billing/enrollments/${enrollmentId}/cycles/${cycle.mi_id}`,
        payload,
      );
      if (data?.changed === false) {
        toast.info('No changes detected');
      } else {
        const extra = data?.cascaded_count > 0 ? ` (+${data.cascaded_count} future cycles updated)` : '';
        toast.success(`Billing cycle updated${extra}`);
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Update failed');
      setConfirmStep(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!submitting) onOpenChange(v); }}>
      <DialogContent className="sm:max-w-md" data-testid="edit-billing-cycle-dialog">
        <DialogHeader>
          <DialogTitle>Edit Billing Cycle</DialogTitle>
          <DialogDescription>
            {blocked
              ? 'This cycle is paid/refunded and cannot be edited — paid history is immutable.'
              : 'Updating this cycle will modify the connected invoice. The enrollment monthly_fee is NOT touched.'}
          </DialogDescription>
        </DialogHeader>

        {blocked && (
          <Alert variant="destructive" className="mb-3" data-testid="edit-cycle-blocked-alert">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Status: <span className="font-semibold">{cycle.display_status || cycle.status}</span>.
              Editing locked.
            </AlertDescription>
          </Alert>
        )}

        {!blocked && isPastCycle && (
          <Alert className="mb-3 bg-amber-50 border-amber-300 text-amber-900" data-testid="edit-cycle-past-warning">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-amber-900">
              You are editing a <span className="font-semibold">PAST</span> billing cycle ({cycle.billing_month}).
              Updating this will modify the historical invoice.
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="edit-amount">Amount ($)</Label>
              <Input
                id="edit-amount"
                type="number"
                step="0.01"
                min="0"
                value={form.amount}
                disabled={blocked || submitting}
                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                data-testid="edit-cycle-amount"
              />
            </div>
            <div>
              <Label htmlFor="edit-due-date">Due Date</Label>
              <Input
                id="edit-due-date"
                type="date"
                value={(form.due_date || '').slice(0, 10)}
                disabled={blocked || submitting}
                onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))}
                data-testid="edit-cycle-due-date"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="edit-billing-month">Billing Month (YYYY-MM)</Label>
            <Input
              id="edit-billing-month"
              type="text"
              placeholder="2026-08"
              maxLength={7}
              value={form.billing_month}
              disabled={blocked || submitting}
              onChange={(e) => setForm((f) => ({ ...f, billing_month: e.target.value }))}
              data-testid="edit-cycle-billing-month"
            />
          </div>

          {/* iter307dc — Frequency dropdown (re-stamps MI only) */}
          <div>
            <Label htmlFor="edit-frequency">Payment Frequency</Label>
            <Select
              value={form.payment_frequency || 'monthly'}
              onValueChange={(v) => setForm((f) => ({ ...f, payment_frequency: v }))}
              disabled={blocked || submitting}
            >
              <SelectTrigger id="edit-frequency" data-testid="edit-cycle-frequency">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="quarterly">Quarterly</SelectItem>
                <SelectItem value="biannual">Bi-Annual</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-slate-500 mt-1">
              Re-stamps this invoice&apos;s frequency only. Enrollment&apos;s cadence is not changed.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="edit-period-start">Period Start</Label>
              <Input
                id="edit-period-start"
                type="date"
                value={(form.period_start || '').slice(0, 10)}
                disabled={blocked || submitting}
                onChange={(e) => setForm((f) => ({ ...f, period_start: e.target.value }))}
                data-testid="edit-cycle-period-start"
              />
            </div>
            <div>
              <Label htmlFor="edit-period-end">Period End</Label>
              <Input
                id="edit-period-end"
                type="date"
                value={(form.period_end || '').slice(0, 10)}
                disabled={blocked || submitting}
                onChange={(e) => setForm((f) => ({ ...f, period_end: e.target.value }))}
                data-testid="edit-cycle-period-end"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="edit-description">Description</Label>
            <Textarea
              id="edit-description"
              rows={2}
              value={form.description}
              disabled={blocked || submitting}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              data-testid="edit-cycle-description"
            />
          </div>

          <label className="flex items-start gap-2 text-sm cursor-pointer pt-1">
            <Checkbox
              checked={cascade}
              disabled={blocked || submitting}
              onCheckedChange={(v) => setCascade(!!v)}
              data-testid="edit-cycle-cascade-toggle"
            />
            <span className="text-slate-700">
              Apply new amount to all <span className="font-semibold">future unpaid</span> cycles for this enrollment too
              <div className="text-xs text-slate-500 mt-0.5">
                Only triggered when amount changes. Enrollment monthly_fee is never modified.
              </div>
            </span>
          </label>

          {confirmStep && !blocked && (
            <Alert className="bg-amber-50 border-amber-300 text-amber-900" data-testid="edit-cycle-confirm-step">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-amber-900">
                Confirm: this will rewrite the invoice
                {cascade ? ' + every future unpaid cycle' : ''}. Click Save again to apply.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" disabled={submitting} onClick={() => onOpenChange(false)} data-testid="edit-cycle-cancel">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={blocked || submitting}
            data-testid="edit-cycle-save"
          >
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {confirmStep ? 'Confirm & Save' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


export const NewBillingCycleDialog = ({ open, onOpenChange, enrollmentId, enrollment, onCreated }) => {
  // iter307dc — Frequency + program-tier-aware amount auto-fill.
  // Programs store THREE per-month prices (price / quarterly_price /
  // biannual_price). The dialog reads the matching tier and computes
  // `cycle_total = tier_pm × multiplier`. Falls back to the
  // enrollment's `base_monthly_fee` when a tier isn't configured.
  const FREQ_MULT = { monthly: 1, quarterly: 3, biannual: 6 };
  const FREQ_LABEL = { monthly: 'every month', quarterly: 'every 3 months', biannual: 'every 6 months' };

  const tierPerMonth = (freq) => {
    const enr = enrollment || {};
    if (freq === 'quarterly' && Number(enr.program_quarterly_price) > 0) {
      return Number(enr.program_quarterly_price);
    }
    if (freq === 'biannual' && Number(enr.program_biannual_price) > 0) {
      return Number(enr.program_biannual_price);
    }
    if (freq === 'monthly' && Number(enr.program_price) > 0) {
      return Number(enr.program_price);
    }
    // Fallback: enrollment's effective per-month base.
    return Number(enr.base_monthly_fee || enr.monthly_fee || 0);
  };

  const defaultCycleAmount = (freq) => {
    const mult = FREQ_MULT[freq] || 1;
    // Preserve any active discount when the requested frequency
    // matches the enrollment's current cadence.
    if (freq === (enrollment?.payment_frequency || 'monthly')
        && Number(enrollment?.cycle_amount_due) > 0) {
      return Number(enrollment.cycle_amount_due);
    }
    const pm = tierPerMonth(freq);
    return Math.round(pm * mult * 100) / 100;
  };

  const [form, setForm] = useState({
    billing_month: '',
    payment_frequency: enrollment?.payment_frequency || 'monthly',
    amount: defaultCycleAmount(enrollment?.payment_frequency || 'monthly'),
    due_date: '',
    description: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmStep, setConfirmStep] = useState(false);

  useEffect(() => {
    if (open) {
      const nbd = (enrollment?.next_billing_date || '').slice(0, 7);
      const freq = enrollment?.payment_frequency || 'monthly';
      setForm({
        billing_month: nbd || '',
        payment_frequency: freq,
        amount: defaultCycleAmount(freq),
        due_date: '',
        description: '',
      });
      setConfirmStep(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, enrollment]);

  const handleFreqChange = (newFreq) => {
    setForm((f) => ({
      ...f,
      payment_frequency: newFreq,
      amount: defaultCycleAmount(newFreq),
    }));
  };

  const handleCreate = async () => {
    if (!form.billing_month || !form.amount) {
      toast.error('billing_month and amount are required');
      return;
    }
    if (!/^\d{4}-\d{2}$/.test(form.billing_month)) {
      toast.error('billing_month must be in YYYY-MM format');
      return;
    }
    if (!confirmStep) {
      setConfirmStep(true);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        billing_month: form.billing_month,
        amount: Number(form.amount),
        due_date: form.due_date || null,
        description: form.description || null,
        // iter307dc — pass freq + multiplier so the backend can snapshot
        // them on the new monthly_invoice for downstream cycle-window
        // matching by the Monthly Dashboard.
        payment_frequency: form.payment_frequency,
        frequency_multiplier: ({monthly: 1, quarterly: 3, biannual: 6}[form.payment_frequency] || 1),
      };
      const { data } = await api.post(
        `/admin/billing/enrollments/${enrollmentId}/cycles`,
        payload,
      );
      toast.success(`New billing cycle + invoice created for ${form.billing_month}`);
      onOpenChange(false);
      onCreated?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Create failed');
      setConfirmStep(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!submitting) onOpenChange(v); }}>
      <DialogContent className="sm:max-w-md" data-testid="new-billing-cycle-dialog">
        <DialogHeader>
          <DialogTitle>New Manual Billing Cycle</DialogTitle>
          <DialogDescription>
            Creates a new billing cycle and a connected unpaid invoice (status: pending).
            Backdating before <span className="font-mono text-xs">{(enrollment?.next_billing_date || '').slice(0, 10) || '—'}</span> is not allowed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label htmlFor="new-billing-month">Billing Month (YYYY-MM)</Label>
            <Input
              id="new-billing-month"
              type="text"
              placeholder="2026-08"
              maxLength={7}
              value={form.billing_month}
              disabled={submitting}
              onChange={(e) => setForm((f) => ({ ...f, billing_month: e.target.value }))}
              data-testid="new-cycle-billing-month"
            />
          </div>

          {/* iter307dc — Frequency + auto-filled cycle amount */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="new-frequency">Payment Frequency</Label>
              <Select
                value={form.payment_frequency}
                onValueChange={handleFreqChange}
                disabled={submitting}
              >
                <SelectTrigger id="new-frequency" data-testid="new-cycle-frequency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="biannual">Bi-Annual</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">
                Cycle covers <strong>{FREQ_LABEL[form.payment_frequency] || 'every month'}</strong>
              </p>
            </div>
            <div>
              <Label htmlFor="new-amount">
                Cycle Amount ($)
                <span className="ml-1 text-[10px] text-slate-400 font-normal">— editable override</span>
              </Label>
              <Input
                id="new-amount"
                type="number"
                step="0.01"
                min="0"
                value={form.amount}
                disabled={submitting}
                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                data-testid="new-cycle-amount"
              />
              {(Number(enrollment?.program_price) > 0
                || Number(enrollment?.program_quarterly_price) > 0
                || Number(enrollment?.program_biannual_price) > 0
                || Number(enrollment?.base_monthly_fee) > 0) && (
                <p className="text-[11px] text-slate-500 mt-1">
                  Tier rate: {cs()}{tierPerMonth(form.payment_frequency).toFixed(2)}/mo
                  {' × '}
                  {FREQ_MULT[form.payment_frequency] || 1}
                  {' = $'}
                  {(tierPerMonth(form.payment_frequency) * (FREQ_MULT[form.payment_frequency] || 1)).toFixed(2)}
                </p>
              )}
            </div>
          </div>

          <div>
            <Label htmlFor="new-due-date">Due Date (optional)</Label>
            <Input
              id="new-due-date"
              type="date"
              value={form.due_date}
              disabled={submitting}
              onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))}
              data-testid="new-cycle-due-date"
            />
          </div>

          <div>
            <Label htmlFor="new-description">Description (optional)</Label>
            <Textarea
              id="new-description"
              rows={2}
              placeholder={`Manual billing cycle · ${form.billing_month || 'YYYY-MM'}`}
              value={form.description}
              disabled={submitting}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              data-testid="new-cycle-description"
            />
          </div>

          {confirmStep && (
            <Alert className="bg-amber-50 border-amber-300 text-amber-900" data-testid="new-cycle-confirm-step">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-amber-900">
                Create new billing cycle for <span className="font-semibold">{form.billing_month}</span> at
                <span className="font-mono"> {cs()}{Number(form.amount || 0).toFixed(2)}</span>?
                A new unpaid invoice will be created for this player.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" disabled={submitting} onClick={() => onOpenChange(false)} data-testid="new-cycle-cancel">
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={submitting} data-testid="new-cycle-create">
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {confirmStep ? 'Confirm & Create' : 'Create Cycle'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};



export const DeleteBillingCycleDialog = ({ open, onOpenChange, enrollmentId, cycle, onDeleted }) => {
  const [submitting, setSubmitting] = useState(false);
  const [confirmStep, setConfirmStep] = useState(false);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) {
      setConfirmStep(false);
      setReason('');
    }
  }, [open]);

  if (!cycle) return null;

  const guard = isCycleDeletable(cycle);
  const isPaid = guard.paid;
  const isProcessing = guard.processing;
  const isAlreadyVoid = guard.alreadyVoid;
  const isFullyRefunded = guard.fullyRefunded;
  const blocked = !guard.ok;

  const handleDelete = async () => {
    if (blocked) return;
    if (!confirmStep) {
      setConfirmStep(true);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.delete(
        `/admin/billing/enrollments/${enrollmentId}/cycles/${cycle.mi_id}`,
        { data: { reason: reason || null } },
      );
      if (data?.restamped) {
        toast.success(`Cycle ${cycle.billing_month} re-stamped to void (data drift healed)`);
      } else {
        toast.success(
          `Cycle for ${cycle.billing_month} voided${data?.parent_credit_ledger_appended ? ' — parent ledger updated' : ''}`,
        );
      }
      onOpenChange(false);
      onDeleted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Delete failed');
      setConfirmStep(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!submitting) onOpenChange(v); }}>
      <DialogContent className="sm:max-w-md" data-testid="delete-billing-cycle-dialog">
        <DialogHeader>
          <DialogTitle>{isAlreadyVoid ? 'Re-Stamp Void Fields' : 'Delete Billing Cycle'}</DialogTitle>
          <DialogDescription>
            {blocked
              ? 'This cycle cannot be deleted in its current state.'
              : isAlreadyVoid
                ? `Re-stamp the void fields on cycle ${cycle.billing_month} to heal data drift?`
                : `Delete the ${cycle.billing_month} billing cycle for ${cs()}${Number(cycle.amount || 0).toFixed(2)}?`}
          </DialogDescription>
        </DialogHeader>

        {/* Paid → redirect to refund flow */}
        {isPaid && (
          <Alert variant="destructive" data-testid="delete-cycle-paid-block">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="font-semibold mb-1">Paid cycles cannot be deleted directly.</div>
              <div className="text-sm">
                Please either issue an <span className="font-semibold">Account Credit</span> from the parent&apos;s profile,
                or process a <span className="font-semibold">Refund</span> via the existing refund flow on the invoice detail page.
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Processing → redirect to Stripe */}
        {isProcessing && (
          <Alert variant="destructive" data-testid="delete-cycle-processing-block">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="font-semibold mb-1">Cycle is processing in Stripe.</div>
              <div className="text-sm">
                Wait for the ACH settlement (1-5 business days), OR cancel the PaymentIntent in the
                Stripe Dashboard first, then come back here.
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Pending/overdue OR already-void (re-stamp) — show void info + reason */}
        {!blocked && (
          <div className="space-y-3">
            {isFullyRefunded && (
              <Alert className="bg-emerald-50 border-emerald-300 text-emerald-900" data-testid="delete-cycle-fully-refunded-info">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-emerald-900 text-sm">
                  This cycle was <span className="font-semibold">fully refunded</span> (net $0.00), so no money is
                  attached. Deleting will <span className="font-semibold">void</span> it and free the calendar slot so a
                  correct cycle can be created.
                </AlertDescription>
              </Alert>
            )}
            {isAlreadyVoid ? (
              <Alert className="bg-slate-50 border-slate-300 text-slate-800" data-testid="delete-cycle-restamp-info">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-slate-800 text-sm">
                  This cycle is <span className="font-semibold">already void</span>. Clicking Delete will re-stamp the
                  void fields (<span className="font-mono">status, voided_at, voided_by, void_reason</span>) to heal any
                  data drift. No new ledger entry will be created.
                </AlertDescription>
              </Alert>
            ) : (
              <Alert className="bg-amber-50 border-amber-300 text-amber-900" data-testid="delete-cycle-void-info">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-amber-900 text-sm">
                  The invoice will be marked <span className="font-semibold">void</span> (soft-delete, row preserved
                  for audit history). A $0 entry will be appended to the parent&apos;s credit ledger as
                  <span className="font-mono"> voided_invoice</span>.
                </AlertDescription>
              </Alert>
            )}

            <div>
              <Label htmlFor="delete-reason">Reason (optional)</Label>
              <Textarea
                id="delete-reason"
                rows={2}
                placeholder={isAlreadyVoid ? "e.g. Fixing data drift, re-stamping after migration" : "e.g. Player withdrew mid-month, duplicate cycle, etc."}
                value={reason}
                disabled={submitting}
                onChange={(e) => setReason(e.target.value)}
                data-testid="delete-cycle-reason"
              />
            </div>

            {confirmStep && (
              <Alert className="bg-red-50 border-red-300 text-red-900" data-testid="delete-cycle-confirm-step">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-red-900">
                  Confirm: this will {isAlreadyVoid ? 're-stamp the void fields' : 'permanently void the cycle'}. Click Delete again to apply.
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" disabled={submitting} onClick={() => onOpenChange(false)} data-testid="delete-cycle-cancel">
            {blocked ? 'Close' : 'Cancel'}
          </Button>
          {!blocked && (
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={submitting}
              data-testid="delete-cycle-confirm"
            >
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {confirmStep ? 'Confirm Void' : 'Delete'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
